import React, { useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { BsPlayFill } from 'react-icons/bs';

// types
import { InteractiveCTAOption, InventoryItemMedia, PlaybackData } from 'types';
// components
import { VideoPlayer } from 'components/videoPlayer';
import ReactionAndComment from './reactionAndComments';
import { AnnotationItem } from 'queries/details/types';
import { PlayerReactions } from './reactionAndComments/PlayerReactions';
import {
  PREVIEW_VIDEO_THUMBNAIL,
  PREVIEW_VIDEO_URL,
} from 'const/landingPageBuilderPreview';

type ContainerProps = {
  width: string;
};

const Container = styled.div<ContainerProps>`
  box-sizing: border-box;
  width: ${({ width }) => width};
  max-width: 100%;

  @media screen and (min-width: 2800px) {
    width: 100%;
  }
`;

const Main = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const ThumbnailsWrapper = styled.div`
  overflow-x: hidden;

  display: flex;
  align-items: center;
  column-gap: 8px;
  cursor: pointer;
  margin: 0 0 20px;
  &:active {
    cursor: grabbing;
  }

  @media screen and (max-width: 768px) {
    overflow-x: auto;
  }
`;

type ThumbnailProps = {
  img: string;
  active: boolean;
};
const Thumbnail = styled.div<ThumbnailProps>`
  position: relative;
  box-sizing: content-box;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  flex-grow: 0;
  background: rgba(0, 0, 0, 0.5);
  background-image: url(${props => props.img});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  box-shadow: ${({ active, theme }) =>
    active ? `inset 0 0 0 4px ${theme.colors.white[100]}` : `none`};
  border-radius: 5px;
  @media screen and (max-width: 520px) {
    width: 48px;
    height: 48px;
  }
`;

const VideoPlay = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

type ChosenImageProps = {
  img: string;
  width: string;
  height: string;
};
const ChosenImage = styled.div<ChosenImageProps>`
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  max-width: calc(100% - 40px);
  flex-shrink: 0;
  flex-grow: 0;
  background: rgba(0, 0, 0, 0.5);
  background-image: url(${props => props.img});
  background-repeat: no-repeat;
  border-radius: 5px;
  background-size: contain;
  background-position: center;
  margin: 0 20px;
`;

export enum InventoryItemMediaType {
  Image = 1,
  Video = 2,
  MainVideo = 3,
}

type Props = {
  media?: InventoryItemMedia[];
  width?: number;
  userId: number;
  orderDetails: any;
  interactiveCTAs: InteractiveCTAOption[];
  isPreview?: boolean;
  design?: any;
  videoRef?: any;
  renderPlayerReactions?: any;
  showContactInfoModal?: boolean;
  openShowContactInfoModal?: () => void;
  annotations?: AnnotationItem[];
  areControlsShown: boolean;
  playbackData: PlaybackData;
  isBuilderPreviewMode?: boolean;
};

export const MediaGallery = ({
  orderDetails,
  media = [],
  interactiveCTAs,
  width = 620,
  userId,
  isPreview = false,
  design,
  videoRef,
  areControlsShown,
  showContactInfoModal,
  playbackData,
  openShowContactInfoModal,
  annotations,
  isBuilderPreviewMode = false,
}: Props) => {
  const mainWidth = width;
  const mainHeight = width * 0.5625;
  const imageWidth = width;
  const imageHeight = width * 0.5625;

  const mainVideo = {
    inventoryItemMediaId: '0',
    itemId: '',
    mediaType: 3,
    mediaSource: '',
  } as InventoryItemMedia;
  const allMedia = [mainVideo, ...media];
  const theme = useTheme();
  const slider = useRef<HTMLDivElement>(null);

  const [mouseDown, setMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [chosenMedia, setChosenMedia] = useState(
    (allMedia[0] as InventoryItemMedia) || ({} as InventoryItemMedia)
  );

  const startDragging = (e: any) => {
    if (!slider || !slider.current) return;
    setMouseDown(true);
    setStartX(e.pageX - slider.current.offsetLeft);
    setScrollLeft(slider.current.scrollLeft);
  };

  const stopDragging = () => {
    setMouseDown(false);
  };

  const dragg = (e: any) => {
    e.preventDefault();
    if (!mouseDown || !slider || !slider.current) return;
    const x = e.pageX - slider.current.offsetLeft;
    const scroll = x - startX;
    slider.current.scrollLeft = scrollLeft - scroll;
  };

  const getThumbnailImageSource = (media: InventoryItemMedia) => {
    let thumbnailImage = '';
    if (media.mediaType === InventoryItemMediaType.MainVideo) {
      thumbnailImage = isBuilderPreviewMode
        ? PREVIEW_VIDEO_THUMBNAIL
        : orderDetails?.video?.thumbnail;
    } else if (media.mediaType === InventoryItemMediaType.Image) {
      thumbnailImage = media.mediaSource;
    } else {
      thumbnailImage = media.thumbnail || '';
    }

    return thumbnailImage;
  };

  return (
    <Container width={`${width}px`} className='media-gallery-container'>
      {!!media.length && (
        <ThumbnailsWrapper
          ref={slider}
          onMouseMove={(e: any) => dragg(e)}
          onMouseDown={(e: any) => startDragging(e)}
          onMouseUp={() => stopDragging()}
          onMouseLeave={() => stopDragging()}
        >
          {allMedia.map((m, i) => {
            return (
              <Thumbnail
                key={i}
                img={getThumbnailImageSource(m)}
                onClick={() => setChosenMedia(m as InventoryItemMedia)}
                active={
                  m.inventoryItemMediaId === chosenMedia.inventoryItemMediaId
                }
              >
                {m.mediaType !== InventoryItemMediaType.Image && (
                  <VideoPlay>
                    <BsPlayFill color={theme.colors.white[100]} size={30} />
                  </VideoPlay>
                )}
              </Thumbnail>
            );
          })}
        </ThumbnailsWrapper>
      )}
      <Main>
        {chosenMedia &&
          chosenMedia.mediaType === InventoryItemMediaType.MainVideo && (
            <>
              {isBuilderPreviewMode ? (
                <VideoPlayer
                  hidePoweredBy
                  videoSource={PREVIEW_VIDEO_URL}
                  videoRef={videoRef}
                  thumbnail={PREVIEW_VIDEO_THUMBNAIL}
                  playerBackgroundColor={design?.playerBackgroundColor}
                  playerIconsColor={design?.playerIconsAndTextColor}
                  playButtonPosition={design?.playButtonPosition}
                  interactiveCTAs={interactiveCTAs}
                  transcript={playbackData?.transcript}
                />
              ) : (
                <VideoPlayer
                  width={`${mainWidth}px`}
                  height={`${mainHeight}px`}
                  videoSource={orderDetails?.video?.videoSource}
                  thumbnail={orderDetails?.video?.thumbnail}
                  videoRef={videoRef}
                  interactiveCTAs={interactiveCTAs}
                  videoId={orderDetails?.videoId}
                  userId={userId}
                  isPreview={isPreview}
                  playerBackgroundColor={design?.playerBackgroundColor}
                  playerIconsColor={design?.playerIconsAndTextColor}
                  playButtonPosition={design?.playButtonPosition}
                  isAutomotive={true}
                  previewAnnotation={annotations}
                  videoChapters={orderDetails?.video?.chapters || []}
                  showContactInfoModal={showContactInfoModal}
                  openShowContactInfoModal={openShowContactInfoModal}
                  hidePoweredBy={orderDetails.hidePoweredBy}
                  transcript={playbackData?.transcript}
                />
              )}
              <PlayerReactions
                hasControls={areControlsShown}
                videoDetails={playbackData?.video}
              />
            </>
          )}
        {chosenMedia &&
          chosenMedia.mediaType === InventoryItemMediaType.Video && (
            <VideoPlayer
              width={`${mainWidth}px`}
              height={`${mainHeight}px`}
              videoSource={chosenMedia.mediaSource}
              thumbnail={chosenMedia.thumbnail || ''}
              videoRef={videoRef}
              interactiveCTAs={[]}
              videoId={0}
              userId={0}
              // set secondary gallery video as preview
              isPreview={true}
              playerBackgroundColor={design?.playerBackgroundColor}
              playerIconsColor={design?.playerIconsAndTextColor}
              playButtonPosition={design?.playButtonPosition}
              showContactInfoModal={showContactInfoModal}
              hidePoweredBy={orderDetails.hidePoweredBy}
              transcript={playbackData?.transcript}
            />
          )}
        {chosenMedia &&
          chosenMedia.mediaType === InventoryItemMediaType.Image && (
            <ChosenImage
              img={chosenMedia.mediaSource}
              width={`${imageWidth}px`}
              height={`${imageHeight}px`}
              className='chosen-image'
            />
          )}
      </Main>
      {!isBuilderPreviewMode && (
        <ReactionAndComment videoRef={videoRef} playbackData={orderDetails} />
      )}
    </Container>
  );
};
